import React from 'react';
import SidebarParametres from '../../../layout/SidebarParametres';
import { useOAD } from '../../../layout/OADLayout';
import { api } from '../../shared/api/api';
import { useApiData } from '../../shared/hooks/useFetch';
import LimiteErreur from '../../shared/components/LimiteErreur';
import ParametresEau3Mois from '../../shared/components/ParametresEau3Mois';
import { useParametresEau3Mois } from '../../shared/hooks/useParametresEau3Mois';
import Eau3MoisTotalChart from '../../shared/charts/Eau3MoisTotalChart';
import { formatNombre } from '../../shared/utils/eau3mois';

/**
 * Eau à 3 mois / Total territoire — consommation du territoire, toutes sources
 * (port du Shiny), en deux cartes : journalière puis cumulée : maïs, vaches laitières, porcs à
 * l'engraissement, tourisme et résidents, sommés trajectoire par trajectoire.
 * Composition fixe : ni sélecteur Culture / animal, ni grandeur à choisir (le
 * maïs est pris en irrigation, seule grandeur additionnable avec les autres).
 * Le cumul d'une culture ou d'un élevage seul est sur la page Cumul culture.
 */
export default function TotalTerritoire() {
  const { idExploit } = useOAD();
  const { rcp, coupure, masquerResidents, modifier } = useParametresEau3Mois();

  const parametres = { rcp, grandeur: 'irrigation', coupure, masquerResidents };
  const hors = masquerResidents ? ' (hors résidents)' : '';
  const { data, loading, error } = useApiData(api.getEau3MoisTotal, parametres, { skip: !coupure });

  return (
    <div className="oad-contenu">
      <SidebarParametres niveau1="annee-en-cours" niveau2="eau-3-mois" niveau3="territoire" idExploit={idExploit}>
        <ParametresEau3Mois avecGrandeur={false} total />
        <label className="sidebar-checkbox">
          <input type="checkbox" checked={masquerResidents} onChange={(e) => modifier({ masquerResidents: e.target.checked })} />
          <span>Masquer les résidents (ils écrasent les autres postes)</span>
        </label>
      </SidebarParametres>
      <main className="oad-cards">
        {error && <p className="tdb-erreur">{error}</p>}
        {loading && <p className="tdb-chargement">Chargement…</p>}
        {data && (
          <>
            <section className="oad-card">
              <h2 className="tdb-section-titre">
                Consommation journalière du territoire — toutes sources{hors}
              </h2>
              <LimiteErreur cle={`total-jour-${JSON.stringify(parametres)}`}>
                <div className="oad-largeur-eau">
                  <Eau3MoisTotalChart
                    origine={data.origine} postes={data.postes} parPoste={data.jour.postes}
                    total={data.jour.total} coupure={data.coupure} unite="m³/jour" decimales={0}
                  />
                </div>
              </LimiteErreur>
            </section>

            <section className="oad-card">
              <h2 className="tdb-section-titre">
                Consommation cumulée du territoire — toutes sources{hors}
              </h2>
              <LimiteErreur cle={`total-cumul-${JSON.stringify(parametres)}`}>
                <div className="oad-largeur-eau">
                  <Eau3MoisTotalChart
                    origine={data.origine} postes={data.postes} parPoste={data.cumul.postes}
                    total={data.cumul.total} coupure={data.coupure} unite="millions de m³" decimales={1}
                  />
                </div>
              </LimiteErreur>
              <p className="ig-tableau-sous-titre" style={{ marginTop: 12 }}>
                Cumul en fin de période, en millions de m³
              </p>
              <table className="oad-data-table oad-table-centered">
                <thead>
                  <tr><th>Poste</th><th>Min</th><th>Médiane</th><th>Max</th><th>Part de la médiane (%)</th></tr>
                </thead>
                <tbody>
                  {data.recap.map((l) => (
                    <tr key={l.poste} style={l.poste === 'TOTAL' ? { fontWeight: 700 } : undefined}>
                      <td>{l.poste}</td>
                      <td>{formatNombre(l.min, 1)}</td>
                      <td>{formatNombre(l.med, 1)}</td>
                      <td>{formatNombre(l.max, 1)}</td>
                      <td>{formatNombre(l.part, 1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
